import React, { useState, useEffect } from 'react';
import { agodaApiService, formatPropertyForDisplay, filterPropertiesByType, sortProperties } from '../../services/agodaApiService';
import { getActiveCityConfigs } from '../../utils/cityConfigUtils';

const PropertyListing = ({ 
  cityId, 
  propertyType = 'all', 
  limit = 20, 
  showFilters = true,
  onPropertySelect,
  className = ''
}) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(cityId);
  
  // Filter states
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minRating: '',
    amenities: [],
    sortBy: 'rating',
    sortOrder: 'desc'
  });
  
  const [filteredProperties, setFilteredProperties] = useState([]);

  // Fetch cities on component mount
  useEffect(() => {
    fetchCities();
  }, []);

  // Fetch properties when city or filters change
  useEffect(() => {
    if (selectedCity) {
      fetchProperties();
    }
  }, [selectedCity, filters]);

  const fetchCities = async () => {
    try {
      const result = await getActiveCityConfigs();
      if (result.success) {
        setCities(result.data);
        if (!selectedCity && result.data.length > 0) {
          setSelectedCity(result.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchProperties = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const city = cities.find(c => c.id === selectedCity);
      if (!city) {
        throw new Error('City not found');
      }

      const options = {
        propertyType,
        limit,
        minPrice: filters.minPrice ? parseInt(filters.minPrice) : undefined,
        maxPrice: filters.maxPrice ? parseInt(filters.maxPrice) : undefined,
        rating: filters.minRating ? parseFloat(filters.minRating) : undefined,
        amenities: filters.amenities,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const result = await agodaApiService.searchProperties(city.agodaSettings.cityId, options);
      
      if (result.success) {
        const formattedProperties = result.data.properties.map(formatPropertyForDisplay);
        setProperties(formattedProperties);
        setFilteredProperties(formattedProperties);
      } else {
        throw new Error(result.error || 'Failed to fetch properties');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFilters(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleCityChange = (newCityId) => {
    setSelectedCity(newCityId);
  };

  const handlePropertyClick = (property) => {
    if (onPropertySelect) {
      onPropertySelect(property);
    }
  };

  const clearFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      minRating: '',
      amenities: [],
      sortBy: 'rating',
      sortOrder: 'desc'
    });
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center h-64 ${className}`}>
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Properties</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchProperties}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* City Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select City
        </label>
        <select
          value={selectedCity || ''}
          onChange={(e) => handleCityChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select a city...</option>
          {cities.map(city => (
            <option key={city.id} value={city.id}>
              {city.cityName} ({city.cityNameEn})
            </option>
          ))}
        </select>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Price (THB)
              </label>
              <input
                type="number"
                value={filters.minPrice}
                onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Min price"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Price (THB)
              </label>
              <input
                type="number"
                value={filters.maxPrice}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Max price"
              />
            </div>
            
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Rating
              </label>
              <select
                value={filters.minRating}
                onChange={(e) => handleFilterChange('minRating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any Rating</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
                <option value="3.5">3.5+ Stars</option>
                <option value="3.0">3.0+ Stars</option>
              </select>
            </div>
            
            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="rating">Rating</option>
                <option value="price">Price</option>
                <option value="reviewCount">Reviews</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>
          
          {/* Amenities */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amenities
            </label>
            <div className="flex flex-wrap gap-2">
              {['WiFi', 'Pool', 'Gym', 'Spa', 'Restaurant', 'Parking', 'Air Conditioning'].map(amenity => (
                <button
                  key={amenity}
                  onClick={() => handleAmenityToggle(amenity)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filters.amenities.includes(amenity)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {amenity}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            onClick={() => handlePropertyClick(property)}
          />
        ))}
      </div>

      {filteredProperties.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties Found</h3>
          <p className="text-gray-600">Try adjusting your filters or select a different city.</p>
        </div>
      )}
    </div>
  );
};

// Property Card Component
const PropertyCard = ({ property, onClick }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer overflow-hidden"
    >
      {/* Property Image */}
      <div className="relative h-48 bg-gray-200">
        {!imageError ? (
          <img
            src={property.primaryImage}
            alt={property.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Property Type Badge */}
        <div className="absolute top-2 left-2">
          <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
            {property.type}
          </span>
        </div>
        
        {/* Rating Badge */}
        <div className="absolute top-2 right-2">
          <span className="bg-white bg-opacity-90 text-gray-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <span className="text-yellow-500">★</span>
            {property.rating}
          </span>
        </div>
      </div>

      {/* Property Info */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
          {property.name}
        </h3>
        
        <p className="text-sm text-gray-600 mb-2 line-clamp-1">
          {property.formattedLocation}
        </p>
        
        <p className="text-sm text-gray-700 mb-3 line-clamp-2">
          {property.description}
        </p>
        
        {/* Amenities */}
        <div className="flex flex-wrap gap-1 mb-3">
          {property.amenities.slice(0, 3).map((amenity, index) => (
            <span
              key={index}
              className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
            >
              {amenity}
            </span>
          ))}
          {property.amenities.length > 3 && (
            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
              +{property.amenities.length - 3} more
            </span>
          )}
        </div>
        
        {/* Price and Actions */}
        <div className="flex justify-between items-center">
          <div>
            <div className="text-lg font-bold text-blue-600">
              {property.pricePerPeriod}
            </div>
            <div className="text-xs text-gray-500">
              {property.reviewCount} reviews
            </div>
          </div>
          
          <div className="flex gap-2">
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm">
              View Details
            </button>
            <button className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm">
              Book Now
            </button>
          </div>
        </div>
        
        {/* Commission Info (for admin) */}
        {property.commissionRate > 0 && (
          <div className="mt-2 text-xs text-green-600">
            Commission: {property.commissionRate * 100}% (฿{property.commissionAmount.toLocaleString()})
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyListing;

